import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Alert } from "@mui/material";
import { Link } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Checkbox,
  FormControlLabel,
  Snackbar,
  MenuItem,
  Select,
  Box,
  FormControl,
  InputLabel,
} from "@mui/material";

import axios from "axios";
import { useSelector } from "react-redux";
import {
  Business,
  LocationOn,
  ContactPhone,
  Work,
  AccountBalance,
  Payment,
} from "@mui/icons-material";

import config from "../config.js";

const steps = [
  "Company Info",
  "Address",
  "Contact Info",
  "Business Info",
  "Tax & Compliance",
  "Banking Info",
];

const companyTypes = [
  "Private Limited",
  "Public Limited",
  "Partnership",
  "Sole Proprietorship",
  "Other",
];

const KycForm = () => {
  const userId = useSelector((state) => state.auth.user?.id);

  const { API_BASE_URL } = config;
  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      companyInfo: {
        legalName: "",
        tradeName: "",
        companyType: "",
        registrationNumber: "",
        dateOfIncorporation: "",
        countryOfIncorporation: "",
      },
      address: {
        registeredOffice: {
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
        },
        operatingOffice: {
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
        },
      },
      contactInfo: {
        primaryContact: {
          name: "",
          position: "",
          phone: "",
          email: "",
        },
        secondaryContact: {
          name: "",
          position: "",
          phone: "",
          email: "",
        },
      },
      businessInfo: {
        natureOfBusiness: "",
        industry: "",
        mainActivities: "",
        productsOrServices: "",
        annualTurnover: "",
        numberOfEmployees: "",
        majorMarkets: "",
      },
      taxInfo: {
        taxIdentificationNumber: "",
        vatGstNumber: "",
        countryOfTaxResidence: "",
      },
      complianceInfo: {
        amlPolicyImplemented: false,
        amlComplianceOfficer: {
          name: "",
          contactInfo: "",
        },
      },
      bankingInfo: [
        {
          bankName: "",
          accountNumber: "",
          bankAddress: "",
          swiftCode: "",
        },
      ],
    },
  });

  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const getFieldsForStep = (step) => {
    switch (step) {
      case 0:
        return [
          "companyInfo.legalName",
          "companyInfo.tradeName",
          "companyInfo.companyType",
          "companyInfo.registrationNumber",
          "companyInfo.dateOfIncorporation",
          "companyInfo.countryOfIncorporation",
        ];
      case 1:
        return [
          "address.registeredOffice.street",
          "address.registeredOffice.city",
          "address.registeredOffice.state",
          "address.registeredOffice.postalCode",
          "address.registeredOffice.country",
          "address.operatingOffice.street",
          "address.operatingOffice.city",
          "address.operatingOffice.state",
          "address.operatingOffice.postalCode",
          "address.operatingOffice.country",
        ];
      case 2:
        return [
          "contactInfo.primaryContact.name",
          "contactInfo.primaryContact.position",
          "contactInfo.primaryContact.phone",
          "contactInfo.primaryContact.email",
          "contactInfo.secondaryContact.name",
          "contactInfo.secondaryContact.position",
          "contactInfo.secondaryContact.phone",
          "contactInfo.secondaryContact.email",
        ];
      case 3:
        return [
          "businessInfo.natureOfBusiness",
          "businessInfo.industry",
          "businessInfo.mainActivities",
          "businessInfo.productsOrServices",
          "businessInfo.annualTurnover",
          "businessInfo.numberOfEmployees",
          "businessInfo.majorMarkets",
        ];
      case 4:
        return [
          "taxInfo.taxIdentificationNumber",
          "taxInfo.vatGstNumber",
          "taxInfo.countryOfTaxResidence",
          "complianceInfo.amlPolicyImplemented",
          "complianceInfo.amlComplianceOfficer.name",
          "complianceInfo.amlComplianceOfficer.contactInfo",
        ];
      case 5:
        return [
          "bankingInfo[0].bankName",
          "bankingInfo[0].accountNumber",
          "bankingInfo[0].bankAddress",
          "bankingInfo[0].swiftCode",
        ];
      default:
        return [];
    }
  };

  const validateStep = async (step) => {
    const fields = getFieldsForStep(step);
    const isValid = await trigger(fields);
    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateStep(activeStep);
    if (isValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      setSnackbar({
        open: true,
        message: "Please fill all required fields correctly.",
        severity: "error",
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!userId) {
        throw new Error("User ID not found. Please log in again.");
      }

      data.userId = userId;

      ["mainActivities", "productsOrServices", "majorMarkets"].forEach(
        (field) => {
          if (
            data.businessInfo[field] &&
            typeof data.businessInfo[field] === "string"
          ) {
            data.businessInfo[field] = data.businessInfo[field]
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item !== "");
          }
        }
      );

      const response = await axios.post(`${API_BASE_URL}/api/v1/kyc`, data);

      if (response.status === 200 || response.status === 201) {
        setSnackbar({
          open: true,
          message: "KYC submitted successfully!",
          severity: "success",
        });
        reset();
        setActiveStep(0);
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      let errorMessage = "An error occurred while submitting KYC.";

      if (error.response) {
        errorMessage = `Server error: ${error.response.status}. ${
          error.response.data.message || ""
        }`;
      } else if (error.request) {
        errorMessage =
          "No response received from server. Please check your internet connection.";
      } else {
        errorMessage = error.message || "Unknown error occurred";
      }

      setSnackbar({
        open: true,
        message: `Error submitting KYC: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Controller
              name="companyInfo.legalName"
              control={control}
              rules={{ required: "Legal Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Legal Name"
                  error={!!errors.companyInfo?.legalName}
                  helperText={errors.companyInfo?.legalName?.message}
                  margin="normal"
                />
              )}
            />
            <Controller
              name="companyInfo.tradeName"
              control={control}
              rules={{ required: "Trade Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Trade Name"
                  error={!!errors.companyInfo?.tradeName}
                  helperText={errors.companyInfo?.tradeName?.message}
                  margin="normal"
                />
              )}
            />
            <FormControl
              fullWidth
              margin="normal"
              error={!!errors.companyInfo?.companyType}
            >
              <InputLabel id="company-type-label">Company Type</InputLabel>
              <Controller
                name="companyInfo.companyType"
                control={control}
                rules={{ required: "Company Type is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="company-type-label"
                    label="Company Type"
                  >
                    <MenuItem value="" disabled>
                      Select a company type
                    </MenuItem>
                    {companyTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.companyInfo?.companyType && (
                <Typography variant="caption" color="error">
                  {errors.companyInfo.companyType.message}
                </Typography>
              )}
            </FormControl>
            <Controller
              name="companyInfo.registrationNumber"
              control={control}
              rules={{ required: "Registration Number is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Registration Number"
                  error={!!errors.companyInfo?.registrationNumber}
                  helperText={errors.companyInfo?.registrationNumber?.message}
                  margin="normal"
                />
              )}
            />
            <Controller
              name="companyInfo.dateOfIncorporation"
              control={control}
              rules={{ required: "Date of Incorporation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Date of Incorporation"
                  type="date"
                  error={!!errors.companyInfo?.dateOfIncorporation}
                  helperText={errors.companyInfo?.dateOfIncorporation?.message}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
            <Controller
              name="companyInfo.countryOfIncorporation"
              control={control}
              rules={{ required: "Country of Incorporation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Country of Incorporation"
                  error={!!errors.companyInfo?.countryOfIncorporation}
                  helperText={
                    errors.companyInfo?.countryOfIncorporation?.message
                  }
                  margin="normal"
                />
              )}
            />
          </>
        );
      case 1:
        return (
          <>
            <Typography variant="h6">Registered Office</Typography>
            {["street", "city", "state", "postalCode", "country"].map(
              (field) => (
                <Controller
                  key={field}
                  name={`address.registeredOffice.${field}`}
                  control={control}
                  rules={{
                    required: `${
                      field.charAt(0).toUpperCase() + field.slice(1)
                    } is required`,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      label={field.charAt(0).toUpperCase() + field.slice(1)}
                      value={value}
                      onChange={onChange}
                      error={!!errors.address?.registeredOffice?.[field]}
                      helperText={
                        errors.address?.registeredOffice?.[field]?.message
                      }
                      margin="normal"
                    />
                  )}
                />
              )
            )}
            <Typography variant="h6" style={{ marginTop: "20px" }}>
              Operating Office
            </Typography>
            {["street", "city", "state", "postalCode", "country"].map(
              (field) => (
                <Controller
                  key={field}
                  name={`address.operatingOffice.${field}`}
                  control={control}
                  rules={{
                    required: `${
                      field.charAt(0).toUpperCase() + field.slice(1)
                    } is required`,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      label={field.charAt(0).toUpperCase() + field.slice(1)}
                      value={value}
                      onChange={onChange}
                      error={!!errors.address?.operatingOffice?.[field]}
                      helperText={
                        errors.address?.operatingOffice?.[field]?.message
                      }
                      margin="normal"
                    />
                  )}
                />
              )
            )}
          </>
        );
      case 2:
        return (
          <>
            <Typography variant="h6">Primary Contact</Typography>
            {["name", "position", "phone", "email"].map((field) => (
              <Controller
                key={field}
                name={`contactInfo.primaryContact.${field}`}
                control={control}
                rules={{
                  required: `${
                    field.charAt(0).toUpperCase() + field.slice(1)
                  } is required`,
                  ...(field === "email" && {
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Invalid email address",
                    },
                  }),
                }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    fullWidth
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={value}
                    onChange={onChange}
                    error={!!errors.contactInfo?.primaryContact?.[field]}
                    helperText={
                      errors.contactInfo?.primaryContact?.[field]?.message
                    }
                    margin="normal"
                    type={field === "email" ? "email" : "text"}
                  />
                )}
              />
            ))}
            <Typography variant="h6" style={{ marginTop: "20px" }}>
              Secondary Contact
            </Typography>
            {["name", "position", "phone", "email"].map((field) => (
              <Controller
                key={field}
                name={`contactInfo.secondaryContact.${field}`}
                control={control}
                rules={{
                  required: `${
                    field.charAt(0).toUpperCase() + field.slice(1)
                  } is required`,
                  ...(field === "email" && {
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Invalid email address",
                    },
                  }),
                }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    fullWidth
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={value}
                    onChange={onChange}
                    error={!!errors.contactInfo?.secondaryContact?.[field]}
                    helperText={
                      errors.contactInfo?.secondaryContact?.[field]?.message
                    }
                    margin="normal"
                    type={field === "email" ? "email" : "text"}
                  />
                )}
              />
            ))}
          </>
        );
      case 3:
        return (
          <>
            <Controller
              name="businessInfo.natureOfBusiness"
              control={control}
              rules={{ required: "Nature of Business is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Nature of Business"
                  error={!!errors.businessInfo?.natureOfBusiness}
                  helperText={errors.businessInfo?.natureOfBusiness?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "off" }}
                />
              )}
            />
            <Controller
              name="businessInfo.industry"
              control={control}
              rules={{ required: "Industry is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Industry"
                  error={!!errors.businessInfo?.industry}
                  helperText={errors.businessInfo?.industry?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "off" }}
                />
              )}
            />
            <Controller
              name="businessInfo.mainActivities"
              control={control}
              rules={{ required: "Main Activities are required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Main Activities (comma-separated)"
                  error={!!errors.businessInfo?.mainActivities}
                  helperText={errors.businessInfo?.mainActivities?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "off" }}
                />
              )}
            />
            <Controller
              name="businessInfo.productsOrServices"
              control={control}
              rules={{ required: "Products or Services are required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Products or Services (comma-separated)"
                  error={!!errors.businessInfo?.productsOrServices}
                  helperText={errors.businessInfo?.productsOrServices?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "off" }}
                />
              )}
            />
            <Controller
              name="businessInfo.annualTurnover"
              control={control}
              rules={{ required: "Annual Turnover is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Annual Turnover"
                  type="number"
                  error={!!errors.businessInfo?.annualTurnover}
                  helperText={errors.businessInfo?.annualTurnover?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "off" }}
                />
              )}
            />
            <Controller
              name="businessInfo.numberOfEmployees"
              control={control}
              rules={{ required: "Number of Employees is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Number of Employees"
                  type="number"
                  error={!!errors.businessInfo?.numberOfEmployees}
                  helperText={errors.businessInfo?.numberOfEmployees?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "off" }}
                />
              )}
            />
            <Controller
              name="businessInfo.majorMarkets"
              control={control}
              rules={{ required: "Major Markets are required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Major Markets (comma-separated)"
                  error={!!errors.businessInfo?.majorMarkets}
                  helperText={errors.businessInfo?.majorMarkets?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "off" }}
                />
              )}
            />
          </>
        );
      case 4:
        return (
          <>
            <Controller
              name="taxInfo.taxIdentificationNumber"
              control={control}
              rules={{ required: "Tax Identification Number is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Tax Identification Number"
                  error={!!errors.taxInfo?.taxIdentificationNumber}
                  helperText={errors.taxInfo?.taxIdentificationNumber?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
            <Controller
              name="taxInfo.vatGstNumber"
              control={control}
              rules={{ required: "VAT/GST Number is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="VAT/GST Number"
                  error={!!errors.taxInfo?.vatGstNumber}
                  helperText={errors.taxInfo?.vatGstNumber?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
            <Controller
              name="taxInfo.countryOfTaxResidence"
              control={control}
              rules={{ required: "Country of Tax Residence is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Country of Tax Residence"
                  error={!!errors.taxInfo?.countryOfTaxResidence}
                  helperText={errors.taxInfo?.countryOfTaxResidence?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
            <Controller
              name="complianceInfo.amlPolicyImplemented"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="AML Policy Implemented"
                />
              )}
            />
            <Controller
              name="complianceInfo.amlComplianceOfficer.name"
              control={control}
              rules={{ required: "AML Compliance Officer Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="AML Compliance Officer Name"
                  error={!!errors.complianceInfo?.amlComplianceOfficer?.name}
                  helperText={
                    errors.complianceInfo?.amlComplianceOfficer?.name?.message
                  }
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
            <Controller
              name="complianceInfo.amlComplianceOfficer.contactInfo"
              control={control}
              rules={{
                required: "AML Compliance Officer Contact Info is required",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="AML Compliance Officer Contact Info"
                  error={
                    !!errors.complianceInfo?.amlComplianceOfficer?.contactInfo
                  }
                  helperText={
                    errors.complianceInfo?.amlComplianceOfficer?.contactInfo
                      ?.message
                  }
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
          </>
        );
      case 5:
        return (
          <>
            <Controller
              name="bankingInfo[0].bankName"
              control={control}
              rules={{ required: "Bank Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Bank Name"
                  error={!!errors.bankingInfo?.[0]?.bankName}
                  helperText={errors.bankingInfo?.[0]?.bankName?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
            <Controller
              name="bankingInfo[0].accountNumber"
              control={control}
              rules={{ required: "Account Number is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Account Number"
                  error={!!errors.bankingInfo?.[0]?.accountNumber}
                  helperText={errors.bankingInfo?.[0]?.accountNumber?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
            <Controller
              name="bankingInfo[0].bankAddress"
              control={control}
              rules={{ required: "Bank Address is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Bank Address"
                  error={!!errors.bankingInfo?.[0]?.bankAddress}
                  helperText={errors.bankingInfo?.[0]?.bankAddress?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
            <Controller
              name="bankingInfo[0].swiftCode"
              control={control}
              rules={{ required: "SWIFT Code is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="SWIFT Code"
                  error={!!errors.bankingInfo?.[0]?.swiftCode}
                  helperText={errors.bankingInfo?.[0]?.swiftCode?.message}
                  margin="normal"
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Container component={Paper} maxWidth="md" sx={{ p: 4, my: 4, mt: 32 }}>
      <Typography variant="h4" align="center" gutterBottom>
        KYC Form
      </Typography>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label, index) => {
          const icons = [
            Business,
            LocationOn,
            ContactPhone,
            Work,
            AccountBalance,
            Payment,
          ];
          const IconComponent = icons[index];
          return (
            <Step key={label}>
              <StepLabel icon={<IconComponent />}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {renderStepContent(activeStep)}
        <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Grid item>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </Button>
          </Grid>
          <Grid item>
            {activeStep === steps.length - 1 ? (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            ) : (
              <Button onClick={handleNext} variant="contained" color="primary">
                Next
              </Button>
            )}
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Box sx={{ mt: 3 }}>
        <Button
          component={Link}
          to="/KycManagement"
          variant="contained"
          fullWidth
          sx={{
            color: "white",
            backgroundColor: "#EB5A3C",
            "&:hover": {
              backgroundColor: "#d4512f",
            },
          }}
        >
          Back to KYC Management
        </Button>
      </Box>
    </Container>
  );
};

export default KycForm;
